import React, { useState, useEffect, useCallback } from 'react';
import './RoofSafety.css';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faAnchor,
  faToolbox,
  faWrench,
  faStairs,
  faGripLines,
  faShield,
  faRuler,
  faExclamationTriangle,
  faCheckCircle,
  faChevronRight,
  faArrowRight,
  faClock,
  faTimes,
  faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import { getAuthHeader, clearToken } from '../../../utils/auth';
import { getApiEndpoint } from '../../../config/api';
import { generateRoofSafetyReport } from './roofSafetyReport';

// Import all safety components
import Cradles from './Cradles/Cradles';
import Ladders from './Ladders/Ladders';
import Walkways from './Walkways/Walkways';
import Handrails from './Handrails/Handrails';
import StaticLines from './Staticlines/Staticlines';
import AnchorPoints from './AnchorPoints/AnchorPoints';
import AbseilSystems from './AbseilSystems/AbseilSystems';

// ─── Constants ───────────────────────────────────────────────────
const STAT_ITEMS = [
  { key: 'anchorPoints',  label: 'Anchor Points',  icon: faAnchor,     endpoint: 'anchor-points',  table: 'anchor_points' },
  { key: 'cradles',       label: 'Cradles',         icon: faToolbox,    endpoint: 'cradles',        table: 'cradles' },
  { key: 'abseilSystems', label: 'Abseil Systems',  icon: faWrench,     endpoint: 'abseil-systems', table: 'davit_bases' },
  { key: 'ladders',       label: 'Ladders',         icon: faStairs,     endpoint: 'ladders',        table: 'ladders' },
  { key: 'walkways',      label: 'Walkways',        icon: faRuler,      endpoint: 'walkways',       table: 'walkways' },
  { key: 'staticLines',   label: 'Static Lines',    icon: faGripLines,  endpoint: 'static-lines',   table: 'static_lines' },
  { key: 'handrails',     label: 'Handrails',       icon: faShield,     endpoint: 'handrails',      table: 'handrails' }
];

const SIDEBAR_SECTIONS = [
  { id: 'dashboard',      title: 'Roof Dashboard',  faIcon: faChartLine,  component: null },
  { id: 'anchorPoints',   title: 'Anchor Points',   faIcon: faAnchor,     component: AnchorPoints },
  { id: 'cradles',        title: 'Cradles',          faIcon: faToolbox,    component: Cradles },
  { id: 'abseilSystems',  title: 'Abseil Systems',   faIcon: faWrench,     component: AbseilSystems },
  { id: 'ladders',        title: 'Ladders',          faIcon: faStairs,     component: Ladders },
  { id: 'walkways',       title: 'Walkways',         faIcon: faRuler,      component: Walkways },
  { id: 'staticLines',    title: 'Static Lines',     faIcon: faGripLines,  component: StaticLines },
  { id: 'handrails',      title: 'Handrails',        faIcon: faShield,     component: Handrails }
];

// Build ModuleSubNav items with pre-rendered FontAwesome icons
const SUB_NAV_ITEMS = SIDEBAR_SECTIONS.map(s => ({
  id: s.id,
  label: s.title,
  icon: <FontAwesomeIcon icon={s.faIcon} />
}));

// ─── Summary Stats Row ──────────────────────────────────────────
const SummaryStats = ({ stats }) => {
  const totalAssets = stats.reduce((sum, s) => sum + s.total, 0);
  const totalFailed = stats.reduce((sum, s) => sum + s.failed, 0);
  const totalPassed = stats.reduce((sum, s) => sum + s.passed, 0);
  const totalDueSoon = stats.reduce((sum, s) => sum + s.dueSoon, 0);
  const complianceRate = totalAssets > 0 ? Math.round((totalPassed / totalAssets) * 100) : 0;

  const summaryCards = [
    { label: 'Total Assets', value: totalAssets, cls: 'summary-blue' },
    { label: 'Compliant', value: totalPassed, cls: 'summary-green' },
    { label: 'Failed', value: totalFailed, cls: 'summary-red' },
    { label: 'Due Soon', value: totalDueSoon, cls: 'summary-amber' },
    { label: 'Compliance', value: `${complianceRate}%`, cls: 'summary-blue' },
  ];

  return (
    <div className="rd-summary-row">
      {summaryCards.map(c => (
        <div key={c.label} className={`rd-summary-card ${c.cls}`}>
          <span className="rd-summary-value">{c.value}</span>
          <span className="rd-summary-label">{c.label}</span>
        </div>
      ))}
    </div>
  );
};

// ─── Equipment Grid ─────────────────────────────────────────────
const EquipmentGrid = ({ stats, onNavigate }) => (
  <div className="rd-section">
    <div className="rd-section-header">
      <h2 className="rd-section-title">Equipment Overview</h2>
    </div>
    <div className="rd-equipment-grid">
      {stats.map(item => (
        <button
          key={item.key}
          className="rd-equip-card"
          onClick={() => onNavigate && onNavigate(item.key)}
          type="button"
        >
          <div className="rd-equip-icon">
            <FontAwesomeIcon icon={item.icon} />
          </div>
          <div className="rd-equip-info">
            <span className="rd-equip-label">{item.label}</span>
            <span className="rd-equip-count">{item.total}</span>
          </div>
          {(() => {
            const untested = Math.max(0, (item.total || 0) - (item.passed || 0) - (item.failed || 0) - (item.dueSoon || 0));
            if (item.failed > 0) return <span className="rd-equip-badge rd-badge-red">{item.failed} failed</span>;
            if (item.dueSoon > 0) return <span className="rd-equip-badge rd-badge-amber">{item.dueSoon} due</span>;
            if (untested > 0) return <span className="rd-equip-badge rd-badge-amber">{untested} untested</span>;
            if (item.total > 0) return <span className="rd-equip-badge rd-badge-green">All clear</span>;
            return null;
          })()}
          <FontAwesomeIcon icon={faChevronRight} className="rd-equip-arrow" />
        </button>
      ))}
    </div>
  </div>
);

// ─── Attention Panel ────────────────────────────────────────────
const AttentionPanel = ({ attentionItems, onNavigate }) => (
  <div className="rd-panel">
    <div className="rd-panel-header">
      <h3 className="rd-panel-title">
        <FontAwesomeIcon icon={faExclamationTriangle} className="rd-panel-icon rd-icon-amber" />
        Requires Attention
      </h3>
    </div>
    <div className="rd-panel-body">
      {attentionItems.length > 0 ? (
        <div className="rd-attention-list">
          {attentionItems.map(item => (
            <button
              key={item.key}
              className="rd-attention-item"
              onClick={() => onNavigate && onNavigate(item.key)}
              type="button"
            >
              <FontAwesomeIcon icon={item.icon} className="rd-attention-icon" />
              <span className="rd-attention-label">{item.label}</span>
              <span className="rd-attention-count">
                {[
                  item.failed > 0 ? `${item.failed} failed` : null,
                  item.untested > 0 ? `${item.untested} untested` : null,
                ].filter(Boolean).join(' · ')}
              </span>
              <FontAwesomeIcon icon={faChevronRight} className="rd-attention-arrow" />
            </button>
          ))}
        </div>
      ) : (
        <div className="rd-all-clear">
          <FontAwesomeIcon icon={faCheckCircle} className="rd-all-clear-icon" />
          <span>All systems operational</span>
          <span className="rd-all-clear-sub">No issues require attention</span>
        </div>
      )}
    </div>
  </div>
);

// ─── Compliance Ring ────────────────────────────────────────────
const CompliancePanel = ({ stats }) => {
  const totalAssets = stats.reduce((sum, s) => sum + s.total, 0);
  const totalPassed = stats.reduce((sum, s) => sum + s.passed, 0);
  const totalFailed = stats.reduce((sum, s) => sum + s.failed, 0);
  const totalDueSoon = stats.reduce((sum, s) => sum + s.dueSoon, 0);
  // Denominator is every asset — Unknown/untested assets drag the rate down
  // rather than being silently excluded.
  const total = totalAssets;
  const totalUntested = Math.max(0, total - totalPassed - totalFailed - totalDueSoon);
  const complianceRate = total > 0 ? Math.round((totalPassed / total) * 100) : 0;

  // SVG donut
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const passedPct = total > 0 ? totalPassed / total : 0;
  const failedPct = total > 0 ? totalFailed / total : 0;
  const duePct = total > 0 ? totalDueSoon / total : 0;

  const passedLen = passedPct * circumference;
  const failedLen = failedPct * circumference;
  const dueLen = duePct * circumference;

  // Untested assets count as a risk — we can't claim Low risk when we don't know the state.
  const riskLevel = totalFailed > 0 ? 'High' : (totalDueSoon > 0 || totalUntested > 0) ? 'Medium' : 'Low';
  const riskCls = totalFailed > 0 ? 'rd-risk-high' : (totalDueSoon > 0 || totalUntested > 0) ? 'rd-risk-medium' : 'rd-risk-low';

  return (
    <div className="rd-panel">
      <div className="rd-panel-header">
        <h3 className="rd-panel-title">
          <FontAwesomeIcon icon={faShield} className="rd-panel-icon rd-icon-blue" />
          Compliance Overview
        </h3>
        <span className={`rd-risk-pill ${riskCls}`}>{riskLevel} Risk</span>
      </div>
      <div className="rd-panel-body rd-compliance-body">
        <div className="rd-donut-wrap">
          <svg viewBox="0 0 128 128" className="rd-donut">
            {/* Background ring */}
            <circle cx="64" cy="64" r={radius} fill="none" stroke="#f1f5f9" strokeWidth="12" />
            {/* Passed */}
            <circle cx="64" cy="64" r={radius} fill="none" stroke="#22c55e" strokeWidth="12"
              strokeDasharray={`${passedLen} ${circumference - passedLen}`}
              strokeDashoffset={circumference * 0.25}
              strokeLinecap="round" />
            {/* Due Soon */}
            <circle cx="64" cy="64" r={radius} fill="none" stroke="#f59e0b" strokeWidth="12"
              strokeDasharray={`${dueLen} ${circumference - dueLen}`}
              strokeDashoffset={circumference * 0.25 - passedLen}
              strokeLinecap="round" />
            {/* Failed */}
            <circle cx="64" cy="64" r={radius} fill="none" stroke="#ef4444" strokeWidth="12"
              strokeDasharray={`${failedLen} ${circumference - failedLen}`}
              strokeDashoffset={circumference * 0.25 - passedLen - dueLen}
              strokeLinecap="round" />
          </svg>
          <div className="rd-donut-center">
            <span className="rd-donut-pct">{complianceRate}%</span>
          </div>
        </div>
        <div className="rd-compliance-legend">
          <div className="rd-legend-row">
            <span className="rd-legend-dot rd-dot-green" />
            <span className="rd-legend-label">Passed</span>
            <span className="rd-legend-value">{totalPassed}</span>
          </div>
          <div className="rd-legend-row">
            <span className="rd-legend-dot rd-dot-amber" />
            <span className="rd-legend-label">Due Soon</span>
            <span className="rd-legend-value">{totalDueSoon}</span>
          </div>
          <div className="rd-legend-row">
            <span className="rd-legend-dot rd-dot-red" />
            <span className="rd-legend-label">Failed</span>
            <span className="rd-legend-value">{totalFailed}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Recent Activity Card (REAL data) ───────────────────────────
const RecentActivityCard = ({ propertyId }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  // Action types to exclude — viewing/navigation events aren't useful here
  const EXCLUDED_TYPES = ['property_viewed', 'document_viewed', 'access_assigned', 'access_removed'];

  useEffect(() => {
    if (!propertyId) return;
    const headers = getAuthHeader();
    const load = async () => {
      try {
        const res = await fetch(
          getApiEndpoint(`/api/properties/${propertyId}/logs?limit=20`),
          { headers }
        );
        if (res.ok) {
          const data = await res.json();
          const filtered = (data.logs || [])
            .filter(log => !EXCLUDED_TYPES.includes(log.action_type))
            .slice(0, 6);
          setActivities(filtered);
        }
      } catch (e) {
        console.error('Failed to load activity logs:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [propertyId]);

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    const diff = now - d;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  const getActionIcon = (type) => {
    const t = (type || '').toLowerCase();
    if (t.includes('delete') || t.includes('fail')) return { icon: faExclamationTriangle, cls: 'rd-act-red' };
    if (t.includes('create') || t.includes('add')) return { icon: faCheckCircle, cls: 'rd-act-green' };
    if (t.includes('update') || t.includes('edit')) return { icon: faClock, cls: 'rd-act-blue' };
    return { icon: faClock, cls: 'rd-act-gray' };
  };

  return (
    <div className="rd-panel rd-activity-panel">
      <div className="rd-panel-header">
        <h3 className="rd-panel-title">
          <FontAwesomeIcon icon={faClock} className="rd-panel-icon rd-icon-gray" />
          Recent Activity
        </h3>
      </div>
      <div className="rd-panel-body">
        {loading ? (
          <div className="rd-activity-skeleton">
            {[1,2,3].map(i => <div key={i} className="rd-skel-row" />)}
          </div>
        ) : activities.length > 0 ? (
          <div className="rd-activity-feed">
            {activities.map((a, i) => {
              const cfg = getActionIcon(a.action_type);
              return (
                <div key={a.id || i} className="rd-activity-item">
                  <span className={`rd-activity-dot ${cfg.cls}`}>
                    <FontAwesomeIcon icon={cfg.icon} />
                  </span>
                  <div className="rd-activity-body">
                    <span className="rd-activity-msg">{a.description || a.action_type}</span>
                    <span className="rd-activity-meta">
                      {a.user_name && <span className="rd-activity-user">{a.user_name}</span>}
                      <span className="rd-activity-time">{formatTime(a.timestamp)}</span>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="rd-empty-activity">
            <FontAwesomeIcon icon={faClock} className="rd-empty-icon" />
            <span>No recent activity</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Alerts Panel (Slideout) ─────────────────────────────────────
const AlertsPanel = ({ open, onClose, propertyId, onNavigate }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const getItemName = (item, category) => {
    return item.name || item.cradle_name || item.system_name || item.location_description || `${category} #${item.id}`;
  };

  const parseNextDate = (item) => {
    for (const f of ['next_inspection_date', 'nextInspectionDate', 'next_test_date', 'nextTestDate']) {
      if (item?.[f]) { const d = new Date(item[f]); if (!isNaN(d.getTime())) return d; }
    }
    return null;
  };

  const fetchAlerts = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    const headers = getAuthHeader();
    const now = new Date();
    const allAlerts = [];

    try {
      const responses = await Promise.all(
        STAT_ITEMS.map(e =>
          fetch(getApiEndpoint(`/api/properties/${propertyId}/${e.endpoint}`), { headers })
            .then(r => r.ok ? r.json() : [])
            .catch(() => [])
        )
      );

      responses.forEach((data, idx) => {
        const arr = Array.isArray(data) ? data : [];
        const category = STAT_ITEMS[idx];

        arr.forEach(item => {
          const status = (item.status || '').toLowerCase();
          const nextDate = parseNextDate(item);
          const itemName = getItemName(item, category.label);
          const location = item.location_description || item.location || '';

          // Failed items
          if (status === 'fail') {
            allAlerts.push({
              id: `${category.key}-${item.id}`,
              severity: 'critical',
              category: category.label,
              categoryKey: category.key,
              assetId: item.id,
              assetTable: category.table,
              icon: category.icon,
              name: itemName,
              location,
              message: 'Failed inspection — requires immediate attention',
              date: item.last_inspection_date || item.updated_at,
            });
          }

          // Overdue items (next date is in the past)
          if (nextDate && nextDate < now && status !== 'fail') {
            const daysOverdue = Math.ceil((now - nextDate) / (1000 * 60 * 60 * 24));
            allAlerts.push({
              id: `${category.key}-${item.id}-overdue`,
              severity: 'critical',
              category: category.label,
              categoryKey: category.key,
              assetId: item.id,
              assetTable: category.table,
              icon: category.icon,
              name: itemName,
              location,
              message: `Inspection overdue by ${daysOverdue} day${daysOverdue !== 1 ? 's' : ''}`,
              date: nextDate.toISOString(),
            });
          }

          // Due soon (within 30 days)
          if (nextDate && status !== 'fail') {
            const diffDays = Math.ceil((nextDate - now) / (1000 * 60 * 60 * 24));
            if (diffDays >= 0 && diffDays <= 30) {
              allAlerts.push({
                id: `${category.key}-${item.id}-due`,
                severity: 'warning',
                category: category.label,
                categoryKey: category.key,
                assetId: item.id,
                assetTable: category.table,
                icon: category.icon,
                name: itemName,
                location,
                message: diffDays === 0 ? 'Inspection due today' : `Inspection due in ${diffDays} day${diffDays !== 1 ? 's' : ''}`,
                date: nextDate.toISOString(),
              });
            }
          }

          // Untested: status is unknown/missing AND no inspection has been logged
          if (!nextDate && status !== 'fail' && status !== 'pass' && status !== 'pending') {
            allAlerts.push({
              id: `${category.key}-${item.id}-untested`,
              severity: 'warning',
              category: category.label,
              categoryKey: category.key,
              assetId: item.id,
              assetTable: category.table,
              icon: category.icon,
              name: itemName,
              location,
              message: 'Not yet tested — status unknown',
              date: item.updated_at || item.created_at,
            });
          }
        });
      });

      // Sort: critical first, then by date
      allAlerts.sort((a, b) => {
        if (a.severity !== b.severity) return a.severity === 'critical' ? -1 : 1;
        return new Date(a.date || 0) - new Date(b.date || 0);
      });

      setAlerts(allAlerts);
    } catch (e) {
      console.error('Failed to load alerts', e);
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    if (open) fetchAlerts();
  }, [open, fetchAlerts]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const criticalCount = alerts.filter(a => a.severity === 'critical').length;
  const warningCount = alerts.filter(a => a.severity === 'warning').length;

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <>
      {open && <div className="rd-alerts-overlay" onClick={onClose} />}
      <div className={`rd-alerts-panel ${open ? 'rd-alerts-open' : ''}`}>
        <div className="rd-alerts-header">
          <div className="rd-alerts-header-top">
            <h2 className="rd-alerts-title">
              <FontAwesomeIcon icon={faExclamationTriangle} className="rd-alerts-title-icon" />
              Roof Safety Alerts
            </h2>
            <button className="rd-alerts-close" onClick={onClose} type="button">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          {!loading && alerts.length > 0 && (
            <div className="rd-alerts-summary">
              {criticalCount > 0 && (
                <span className="rd-alerts-pill rd-alerts-pill-red">
                  {criticalCount} Critical
                </span>
              )}
              {warningCount > 0 && (
                <span className="rd-alerts-pill rd-alerts-pill-amber">
                  {warningCount} Warning
                </span>
              )}
            </div>
          )}
        </div>

        <div className="rd-alerts-body">
          {loading ? (
            <div className="rd-alerts-loading">
              {[1,2,3,4].map(i => (
                <div key={i} className="rd-alerts-skel">
                  <div className="rd-alerts-skel-icon" />
                  <div className="rd-alerts-skel-lines">
                    <div className="rd-alerts-skel-line rd-skel-w60" />
                    <div className="rd-alerts-skel-line rd-skel-w80" />
                  </div>
                </div>
              ))}
            </div>
          ) : alerts.length === 0 ? (
            <div className="rd-alerts-empty">
              <FontAwesomeIcon icon={faCheckCircle} className="rd-alerts-empty-icon" />
              <span className="rd-alerts-empty-title">All Clear</span>
              <span className="rd-alerts-empty-desc">No failed, overdue, due-soon, or untested items. All roof safety equipment is compliant.</span>
            </div>
          ) : (
            <div className="rd-alerts-list">
              {alerts.map(alert => (
                <button
                  key={alert.id}
                  className={`rd-alert-item ${alert.severity === 'critical' ? 'rd-alert-critical' : 'rd-alert-warning'}`}
                  onClick={() => {
                    onClose();
                    onNavigate(alert.categoryKey);
                    // After the asset-list page mounts, tell it to open the detail modal for this asset.
                    if (alert.assetId && alert.assetTable) {
                      setTimeout(() => {
                        window.dispatchEvent(new CustomEvent('openAssetFromHotspot', {
                          detail: { assetId: alert.assetId, assetTable: alert.assetTable }
                        }));
                      }, 150);
                    }
                  }}
                  type="button"
                >
                  <span className={`rd-alert-icon-wrap ${alert.severity === 'critical' ? 'rd-alert-icon-red' : 'rd-alert-icon-amber'}`}>
                    <FontAwesomeIcon icon={alert.severity === 'critical' ? faExclamationTriangle : faCalendarAlt} />
                  </span>
                  <div className="rd-alert-body">
                    <span className="rd-alert-name">{alert.name}</span>
                    <span className="rd-alert-msg">{alert.message}</span>
                    <span className="rd-alert-meta">
                      <span className="rd-alert-category">{alert.category}</span>
                      {alert.location && <span className="rd-alert-loc">{alert.location}</span>}
                      {alert.date && <span className="rd-alert-date">{formatDate(alert.date)}</span>}
                    </span>
                  </div>
                  <FontAwesomeIcon icon={faChevronRight} className="rd-alert-arrow" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// ─── Dashboard ───────────────────────────────────────────────────
const RoofSafetyDashboard = ({ onNavigate }) => {
  const { id: propertyId } = useParams();
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [reportGenerating, setReportGenerating] = useState(false);
  const [stats, setStats] = useState(() =>
    STAT_ITEMS.map(s => ({ ...s, total: 0, failed: 0, passed: 0, dueSoon: 0 }))
  );

  const handleGenerateReport = async () => {
    if (reportGenerating) return;
    setReportGenerating(true);
    try {
      await generateRoofSafetyReport(propertyId);
    } catch (e) {
      console.error('Report generation failed:', e);
    } finally {
      setReportGenerating(false);
    }
  };

  useEffect(() => {
    const headers = getAuthHeader();
    const parseNextDate = (item) => {
      for (const f of ['next_inspection_date', 'nextTestDate', 'next_test_date']) {
        if (item?.[f]) { const d = new Date(item[f]); if (!isNaN(d.getTime())) return d; }
      }
      for (const lf of ['last_inspection_date', 'lastTestDate']) {
        if (item?.[lf]) {
          const d = new Date(item[lf]);
          if (!isNaN(d.getTime())) { d.setMonth(d.getMonth() + 6); return d; }
        }
      }
      return null;
    };

    const fetchCounts = async () => {
      try {
        const responses = await Promise.all(
          STAT_ITEMS.map(e =>
            fetch(getApiEndpoint(`/api/properties/${propertyId}/${e.endpoint}`), { headers })
          )
        );
        const dataArrays = await Promise.all(responses.map(r => r.ok ? r.json() : []));
        const now = new Date();

        setStats(STAT_ITEMS.map((item, idx) => {
          const arr = Array.isArray(dataArrays[idx]) ? dataArrays[idx] : [];
          const failedCount = arr.filter(x => (x.status || '').toLowerCase() === 'fail').length;
          const passedCount = arr.filter(x => (x.status || '').toLowerCase() === 'pass').length;
          const dueSoonCount = arr.filter(x => {
            if ((x.status || '').toLowerCase() === 'fail') return false;
            const nd = parseNextDate(x);
            if (!nd) return false;
            const diffDays = Math.ceil((nd - now) / (1000 * 60 * 60 * 24));
            return diffDays >= 0 && diffDays <= 30;
          }).length;
          return { ...item, total: arr.length, failed: failedCount, passed: passedCount, dueSoon: dueSoonCount };
        }));
      } catch (e) {
        console.error('Failed to load roof safety counts', e);
      }
    };
    if (propertyId) fetchCounts();
  }, [propertyId]);

  const attentionItems = stats
    .map(item => {
      const untested = Math.max(0, (item.total || 0) - (item.passed || 0) - (item.failed || 0) - (item.dueSoon || 0));
      return { ...item, untested };
    })
    .filter(item => item.failed > 0 || item.untested > 0);

  return (
    <div className="rd-wrapper">
      <div className="rd-header">
        <h1 className="rd-title">Roof Safety</h1>
        <div className="rd-header-actions">
          {(() => {
            const totalFailed = stats.reduce((s, i) => s + i.failed, 0);
            const totalDueSoon = stats.reduce((s, i) => s + i.dueSoon, 0);
            const totalUntested = stats.reduce((s, i) => s + Math.max(0, (i.total || 0) - (i.passed || 0) - (i.failed || 0) - (i.dueSoon || 0)), 0);
            const totalIssues = totalFailed + totalDueSoon + totalUntested;
            // Red pulse: any failures, or a large backlog of untested items.
            // Orange pulse: a handful of warnings (due-soon or untested).
            let pulseClass = '';
            if (totalFailed > 0 || totalUntested >= 10) pulseClass = 'rd-alerts-pulse-red';
            else if (totalDueSoon > 0 || totalUntested > 0) pulseClass = 'rd-alerts-pulse-orange';
            return (
              <button
                className={`rs-btn rs-btn-secondary rd-alerts-btn ${pulseClass}`}
                onClick={() => setAlertsOpen(true)}
                type="button"
              >
                <FontAwesomeIcon icon={faExclamationTriangle} /> Alerts
                {totalIssues > 0 && (
                  <span className="rd-alerts-btn-badge">{totalIssues}</span>
                )}
              </button>
            );
          })()}
          <button className="rs-btn rs-btn-primary" onClick={handleGenerateReport} disabled={reportGenerating} type="button">
            <FontAwesomeIcon icon={faArrowRight} spin={reportGenerating} /> {reportGenerating ? 'Generating...' : 'Report'}
          </button>
        </div>
      </div>

      <SummaryStats stats={stats} />

      <div className="rd-grid-2col">
        <EquipmentGrid stats={stats} onNavigate={onNavigate} />
        <div className="rd-right-stack">
          <CompliancePanel stats={stats} />
          <AttentionPanel attentionItems={attentionItems} onNavigate={onNavigate} />
        </div>
      </div>

      <RecentActivityCard propertyId={propertyId} />

      <AlertsPanel
        open={alertsOpen}
        onClose={() => setAlertsOpen(false)}
        propertyId={propertyId}
        onNavigate={(key) => { setAlertsOpen(false); onNavigate(key); }}
      />
    </div>
  );
};

// ─── Main Shell ──────────────────────────────────────────────────
export default function RoofSafety() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  // Read active sub-section from parent layout context
  const ctx = useOutletContext() || {};
  const activeSubSection = ctx.activeSubSection || 'dashboard';
  const setActiveSubSection = ctx.setActiveSubSection || (() => {});

  // Server connectivity check
  useEffect(() => {
    const checkServer = async () => {
      try {
        const headers = getAuthHeader();
        if (!Object.keys(headers).length) { navigate('/login'); return; }

        const response = await fetch(getApiEndpoint(`/api/properties/${id}`), {
          headers: { ...headers, 'Content-Type': 'application/json' }
        });

        if (response.status === 401) { clearToken(); navigate('/login'); return; }
        if (!response.ok) setError('Cannot connect to the server. Please check if the server is running.');
      } catch (err) {
        console.error('Server connectivity error:', err);
        setError('Cannot connect to the server. Please check if the server is running.');
      }
    };
    checkServer();
  }, [id, navigate]);

  const renderContent = () => {
    if (activeSubSection === 'dashboard') return <RoofSafetyDashboard onNavigate={setActiveSubSection} />;
    const section = SIDEBAR_SECTIONS.find(s => s.id === activeSubSection);
    if (!section?.component) return <RoofSafetyDashboard onNavigate={setActiveSubSection} />;
    const Component = section.component;
    return <Component propertyId={id} />;
  };

  if (error) {
    return (
      <div className="roof-safety-container">
        <div className="roof-safety-content">
          <div className="roof-safety-main">
            <div className="rs-error-alert">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <span>{error}</span>
              <button onClick={() => window.location.reload()}>Retry</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const visiblePills = activeSubSection === 'dashboard'
    ? SUB_NAV_ITEMS.filter(i => i.id === 'dashboard')
    : SUB_NAV_ITEMS;

  const pillNav = (
    <nav className="rs-subnav">
      {visiblePills.map((item) => (
        <button
          key={item.id}
          className={`rs-pill${activeSubSection === item.id ? ' rs-pill-active' : ''}`}
          onClick={() => setActiveSubSection(item.id)}
        >
          {item.icon}
          <span>{item.label}</span>
        </button>
      ))}
    </nav>
  );

  return (
    <div className="roof-safety-container">
      {/* Mobile: pills above the content card */}
      <div className="rs-subnav-mobile-wrap">{pillNav}</div>

      {/* Main Content */}
      <div className="roof-safety-content">
        <div className="roof-safety-main">
          {/* Desktop: pills inside the white card */}
          <div className="rs-subnav-desktop-wrap">{pillNav}</div>
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
